export const INTEGRATION_TYPE = {
  DELIVERY: "delivery",
  CRM: "crm",
} as const;

export type IntegrationType =
  (typeof INTEGRATION_TYPE)[keyof typeof INTEGRATION_TYPE];

export const INTEGRATION_PROVIDER = {
  NCM: "ncm",
  PATHAO: "pathao",
  CLINICSOFT: "clinicsoft",
  PICKNDROP: "pickndrop",
} as const;

export type IntegrationProvider =
  (typeof INTEGRATION_PROVIDER)[keyof typeof INTEGRATION_PROVIDER];

export const DELIVERY_STATUS = {
  PENDING: "pending",
  CREATED: "created",
  PICKED_UP: "picked_up",
  IN_TRANSIT: "in_transit",
  OUT_FOR_DELIVERY: "out_for_delivery",
  DELIVERED: "delivered",
  CANCELLED: "cancelled",
  RETURNED: "returned",
  FAILED: "failed",
} as const;

export type DeliveryStatus =
  (typeof DELIVERY_STATUS)[keyof typeof DELIVERY_STATUS];

export type ProviderInfo = {
  provider: string;
  type: IntegrationType;
  name: string;
  description: string;
  logo: string;
  regions: string[];
  organizationTypes: string[];
  configSchema: Record<
    string,
    {
      type: string;
      required: boolean;
      description?: string;
    }
  >;
};

export type Integration = {
  id: string;
  type: IntegrationType;
  provider: IntegrationProvider;
  name: string;
  isActive: boolean;
  /** Webhook deliveries applied so far. */
  eventsProcessed: number;
  /** When the latest webhook delivery arrived; null until the first one. */
  lastEventAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type IntegrationDetail = Integration & {
  config: Record<string, string>;
  webhookUrl?: string;
  webhookSecret?: string;
  identifier?: string;
};

export type NcmConfig = {
  apiToken: string;
  defaultPickupBranch?: string;
  defaultDeliveryFee?: number;
};

export type PathaoConfig = {
  clientId: string;
  clientSecret: string;
  username: string;
  password: string;
  webhookSecret: string;
  storeId: number;
};

export type ClinicsoftConfig = {
  baseUrl: string;
  apiKey: string;
  username: string;
  password: string;
  permitNo: string;
};

/** Clinic-local calendar days, YYYY-MM-DD, inclusive on both ends. */
export type CreateBillingSyncPayload = {
  fromDate: string;
  toDate: string;
};

export type CreateBillingSyncVariables = {
  integrationId: string;
  payload: CreateBillingSyncPayload;
};

export type PickupNotificationResponse = {
  externalId: string;
  vendorAddress: string;
  requestedAt: string;
};

export type PickndropConfig = {
  apiKey: string;
  apiSecret: string;
  defaultPickupBranch?: string;
  enablePickupNotification?: boolean | string;
  lastPickupNotification?: PickupNotificationResponse;
};

export type PickndropBranch = {
  name: string;
  branch_name: string;
  area: string[];
  status: string;
  branch_type: string;
  branch_code: string;
};

export type CreateIntegrationPayload = {
  type: IntegrationType;
  provider: IntegrationProvider;
  name: string;
  config: Record<string, string>;
  webhookUrl?: string;
  identifier?: string;
};

export type UpdateIntegrationPayload = {
  name?: string;
  config?: Record<string, string>;
  isActive?: boolean;
};

export type IntegrationFilter = {
  type?: IntegrationType;
};

export interface IntegrationTestPayload {
  type: IntegrationType;
  provider: IntegrationProvider;
  config: Record<string, unknown>;
  identifier?: string;
}

export type ProviderStore = {
  storeId: number;
  storeName: string;
  storeAddress: string;
  isActive: boolean;
};

export interface IntegrationTestResponse {
  success: boolean;
  message: string;
  config?: {
    webhookUrl?: string;
    identifier?: string;
    stores?: ProviderStore[];
  };
}

export type NcmBranch = {
  name: string;
  externalId: number;
  externalCode: string;
  coordinates: string | null;
  address: string;
  phone: string;
  branchType: string;
  areasCovered: string | null;
  provinceName: string;
  districtName: string;
};

export type DeliveryPriceEstimateParams = {
  integrationId: string;
  payload: Record<string, unknown>;
  enabled?: boolean;
};

export type DeliveryPriceEstimateResponse = {
  price: number;
  discount: number;
  finalPrice: number;
  codEnabled: boolean;
  codPercentage: number;
  additionalCharge: number;
  rawResponse: Record<string, unknown>;
};

// Re-export config types from constants
export type {
  FieldConfig,
  ProviderRegistryEntry,
  StepConfig,
} from "@/constants/integration";
